import {
  BlockAccessPointDto,
  CreateBlockAccessPointRequest,
  UpdateBlockAccessPointRequest,
} from '@yard/app/api'
import { blockAccessPointsService } from '@yard/services/block-access-points.service'
import { RoadNetworkStore } from '@yard/stores/road-network.store'
import { YardBlockStore } from '@yard/stores/YardBlockStore'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import {
  getBayGapInfo,
  resolveAccessPointBarRect,
} from '../../helpers/block-access-point-bar.utils'

export class BlockAccessPointsViewStore {
  isAccessPointDialogOpen = false
  accessPointDialogBlockId: string | null = null
  accessPointDialogMode: 'add' | 'edit' | null = null
  accessPointDialogEditId: string | null = null

  constructor(
    private readonly yardBlockStore: YardBlockStore,
    private readonly roadNetworkStore: RoadNetworkStore,
  ) {
    makeObservable(this, {
      isAccessPointDialogOpen: observable,
      accessPointDialogBlockId: observable,
      accessPointDialogMode: observable,
      accessPointDialogEditId: observable,

      openAddAccessPointDialog: action,
      openEditAccessPointDialog: action,
      closeAccessPointDialog: action,

      accessPointDialogBlock: computed,
    })
  }

  openAddAccessPointDialog = (blockId: string) => {
    this.isAccessPointDialogOpen = true
    this.accessPointDialogBlockId = blockId
    this.accessPointDialogMode = 'add'
    this.accessPointDialogEditId = null
  }

  openEditAccessPointDialog = (blockId: string, accessPointId: string) => {
    this.isAccessPointDialogOpen = true
    this.accessPointDialogBlockId = blockId
    this.accessPointDialogMode = 'edit'
    this.accessPointDialogEditId = accessPointId
  }

  closeAccessPointDialog = () => {
    this.isAccessPointDialogOpen = false
    this.accessPointDialogBlockId = null
    this.accessPointDialogMode = null
    this.accessPointDialogEditId = null
  }

  get accessPointDialogBlock() {
    return this.accessPointDialogBlockId
      ? this.yardBlockStore.yardBlocks[this.accessPointDialogBlockId]
      : undefined
  }

  private computeRoadNodePositionForAccessPoint(
    blockId: string,
    accessPoint: Pick<BlockAccessPointDto, 'side' | 'minBay' | 'maxBay' | 'minRow' | 'maxRow'>,
  ): { x: number; y: number } | null {
    const block = this.yardBlockStore.yardBlocks[blockId]
    if (!block) return null
    const bayGaps = getBayGapInfo(block.configAreas, block.maxBays)
    const rect = resolveAccessPointBarRect(
      accessPoint,
      { maxBays: block.maxBays, maxRows: block.maxRows },
      bayGaps,
    )
    return {
      x: Math.round(block.layoutPosition.x + rect.x + rect.width / 2),
      y: Math.round(block.layoutPosition.y + rect.y + rect.height / 2),
    }
  }

  private updateAccessPointsInStore(blockId: string, accessPoints: BlockAccessPointDto[]) {
    const block = this.yardBlockStore.yardBlocks[blockId]
    if (!block) return
    this.yardBlockStore.updateStoreItem({ ...block, accessPoints }, blockId)
  }

  async createAccessPoints(request: CreateBlockAccessPointRequest): Promise<BlockAccessPointDto[]> {
    const blockId = request.blockId
    const block = this.yardBlockStore.yardBlocks[blockId]
    if (!block) throw new Error(`Block ${blockId} not found`)

    const optimisticEntries: BlockAccessPointDto[] = request.items.map(item => ({
      id: crypto.randomUUID(),
      side: request.side,
      minBay: item.minBay ?? null,
      maxBay: item.maxBay ?? null,
      minRow: item.minRow ?? null,
      maxRow: item.maxRow ?? null,
    }))
    const previousAccessPoints = block.accessPoints ?? []
    this.updateAccessPointsInStore(blockId, [...previousAccessPoints, ...optimisticEntries])

    try {
      const created = await blockAccessPointsService.create(request)
      runInAction(() => {
        const current = this.yardBlockStore.yardBlocks[blockId]
        if (!current) return
        const optimisticIds = new Set(optimisticEntries.map(e => e.id))
        const preserved = (current.accessPoints ?? []).filter(ap => !optimisticIds.has(ap.id))
        this.updateAccessPointsInStore(blockId, [...preserved, ...created])
      })
      return created
    } catch (err) {
      runInAction(() => {
        this.updateAccessPointsInStore(blockId, previousAccessPoints)
      })
      throw err
    }
  }

  async updateAccessPoint(blockId: string, request: UpdateBlockAccessPointRequest): Promise<void> {
    const block = this.yardBlockStore.yardBlocks[blockId]
    if (!block) return

    const previousAccessPoints = block.accessPoints ?? []
    const optimisticAccessPoints = previousAccessPoints.map(ap =>
      ap.id === request.id ? { ...ap, ...request } : ap,
    )
    this.updateAccessPointsInStore(blockId, optimisticAccessPoints)

    const linkedNode = this.roadNetworkStore.nodes.find(n => n.blockAccessPointId === request.id)
    const newPosition = linkedNode
      ? this.computeRoadNodePositionForAccessPoint(blockId, request)
      : null
    const updatedNode =
      linkedNode && newPosition
        ? { ...linkedNode, layoutPosition: { ...linkedNode.layoutPosition, ...newPosition } }
        : null

    if (updatedNode) this.roadNetworkStore.updateNodeOptimistic(updatedNode)

    try {
      await blockAccessPointsService.update(request)
      if (updatedNode) await this.roadNetworkStore.updateNode(updatedNode)
    } catch (err) {
      runInAction(() => {
        this.updateAccessPointsInStore(blockId, previousAccessPoints)
        if (linkedNode) this.roadNetworkStore.updateNodeOptimistic(linkedNode)
      })
      throw err
    }
  }

  recalculateAccessPointNodePositions(blockId: string): void {
    const block = this.yardBlockStore.yardBlocks[blockId]
    if (!block) return

    const accessPoints = block.accessPoints ?? []
    if (accessPoints.length === 0) return

    const accessPointIds = new Set(accessPoints.map(ap => ap.id))
    const linkedNodes = this.roadNetworkStore.nodes.filter(
      n => n.blockAccessPointId != null && accessPointIds.has(n.blockAccessPointId),
    )

    for (const node of linkedNodes) {
      const accessPoint = accessPoints.find(ap => ap.id === node.blockAccessPointId)
      if (!accessPoint) continue

      const newPosition = this.computeRoadNodePositionForAccessPoint(blockId, accessPoint)
      if (!newPosition) continue
      if (newPosition.x === node.layoutPosition.x && newPosition.y === node.layoutPosition.y)
        continue

      const updatedNode = {
        ...node,
        layoutPosition: { ...node.layoutPosition, ...newPosition },
      }
      this.roadNetworkStore.updateNodeOptimistic(updatedNode)
      this.roadNetworkStore.debouncedUpdateNode(node.id)
    }
  }

  async deleteAccessPoint(id: string, blockId: string): Promise<void> {
    const block = this.yardBlockStore.yardBlocks[blockId]
    if (!block) return

    const previousAccessPoints = block.accessPoints ?? []
    this.updateAccessPointsInStore(
      blockId,
      previousAccessPoints.filter(ap => ap.id !== id),
    )

    const linkedNode = this.roadNetworkStore.nodes.find(n => n.blockAccessPointId === id)
    if (linkedNode) this.roadNetworkStore.removeNodeOptimistic(linkedNode.id)

    try {
      await blockAccessPointsService.delete(id)
      if (linkedNode) await this.roadNetworkStore.deleteNode(linkedNode.id)
    } catch (err) {
      runInAction(() => {
        this.updateAccessPointsInStore(blockId, previousAccessPoints)
        if (linkedNode) this.roadNetworkStore.addNodeOptimistic(linkedNode)
      })
      throw err
    }
  }
}
